/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { AssetEventType, MediaType } from "../enums";
import { IMediaModel, IUserInfoModel } from "../models";

import { TimeHelper } from "./timeHelper";

const scheduledLiveScreenMediaTypes = new Set<string>([
  MediaType.Live,
  MediaType.Match,
  MediaType.Event,
  MediaType.FootballEvent,
  MediaType.Program,
  MediaType.Premiere,
  AssetEventType.MovieNight,
]);

export class MediaHelper {
  static isScheduledLiveScreenMediaType(mediaType?: string): boolean {
    return !!mediaType && scheduledLiveScreenMediaTypes.has(mediaType);
  }

  /**
   * Check if user has access to product. If user bought that product or has subscription to package.
   * @param user `IUserInfoModel` is stored in `IAppState.auth.user`
   * @param productId
   */
  static isUserOwnMedia(user?: IUserInfoModel, media?: IMediaModel): boolean {
    if (media?.IsFree) {
      return true;
    }

    if (!media || !media.Products || media.Products.length === 0) {
      return false;
    }

    if (!user || !user.Products || user.Products.length === 0) {
      return false;
    }

    const mediaProducts = media?.Products ?? [];
    const userProducts = user?.Products ?? [];
    return !!userProducts.some(
      (up) => mediaProducts.findIndex((mp) => mp.Id === up.Id) >= 0,
    );
  }

  /**
   * Check if user has access to product. If user bought that product or has subscription to package.
   * @param user `IUserInfoModel` is stored in `IAppState.auth.user`
   * @param productId
   */
  static getUserMediaExpirationPeriod(
    user?: IUserInfoModel,
    media?: IMediaModel,
  ): string | null {
    if (media?.IsFree) {
      return null;
    }

    if (!media || !media.Products || media.Products.length === 0) {
      return null;
    }

    if (!user || !user.Products || user.Products.length === 0) {
      return null;
    }

    const mediaProductIds = new Set(media.Products.map((row) => row.Id));
    const userProducts = user?.Products ?? [];
    const product = userProducts.find((row) => {
      return mediaProductIds.has(row.Id);
    });

    if (!product || !product.AvailableTo) {
      return null;
    }

    const currentDate = TimeHelper.getCurrentDateTime();
    const availableToDate = TimeHelper.getDateTime(product.AvailableTo);

    const days = TimeHelper.diff(availableToDate, currentDate, "day");
    if (days >= 2) {
      return `${Math.abs(days)}d`;
    }

    const hours = TimeHelper.diff(availableToDate, currentDate, "hour");
    if (hours !== 0) {
      return `${Math.abs(hours)}h`;
    }

    const minutes = TimeHelper.diff(availableToDate, currentDate, "minute");
    if (minutes !== 0) {
      return `${Math.abs(minutes)}m`;
    }

    const seconds = TimeHelper.diff(availableToDate, currentDate, "second");
    if (seconds !== 0) {
      return `${Math.abs(seconds)}s`;
    }

    return null;
  }
}
